import { type BaseAuthService, extractResponseFromError } from "@overmap-ai/core"
import superagent from "superagent"

import type { RootState } from "../store"
import type { AppSDK } from "./sdk"

export function getFilenameFromResponse(response: superagent.Response, fallback: string): string {
	const contentDisposition: string | undefined = response.headers["content-disposition"]

	if (!contentDisposition) return fallback

	const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i)

	if (utf8Match) {
		return decodeURIComponent(utf8Match[1])
	}

	const filenameMatch = contentDisposition.match(/filename="?([^";]+)"?/i)

	return filenameMatch?.[1] ?? fallback
}

/**
 * Sends a request that was built by hand rather than through `performRequest`, with this deployment's credentials.
 *
 * The export endpoints stream a PDF or a CSV straight to `saveAs`, so they build their own superagent request and miss
 * everything `performRequest` does on the way out. The credential is a cookie that only `applyAuth` can attach, and an
 * unsafe method additionally needs the CSRF token that `prepareAuth` guarantees exists.
 *
 * On the way back it gives an authentication failure the treatment `performRequest` would: a session that has ended
 * has to put the "session expired" dialog up, and a rejected CSRF token has to be marked stale so that the next attempt
 * fetches a fresh one. Neither would happen if the raw superagent error were let through.
 *
 * NOTE: The request arrives as a function rather than as a value, and that is not a style choice. A superagent
 *  `Request` is a thenable, so returning one from an `async` function hands it to the promise machinery, which sends
 *  it -- before `prepareAuth` has finished, and with no way for the caller to add a payload. Building it inside the
 *  callback keeps it unsent until there are credentials to put on it.
 * @param auth The SDK's auth service.
 * @param build Builds the request, including its payload and response type.
 * @returns The response.
 * @throws APIError If the request failed because it was not authenticated; see `handleUnauthorized`.
 */
export async function sendAuthenticated(
	auth: BaseAuthService<RootState, AppSDK>,
	build: () => superagent.SuperAgentRequest,
): Promise<superagent.Response> {
	await auth.prepareAuth()
	const req = auth.applyAuth(build())

	try {
		return await req
	} catch (error: unknown) {
		const response = extractResponseFromError(error)
		// `handleUnauthorized` throws an `APIError` that says what went wrong in the user's terms. No retry here: an
		// export is something the user clicked, and can click again once the dialog or the fresh token has done its
		// work.
		if (response && auth.isAuthFailure(response)) await auth.handleUnauthorized(req, response)
		throw error
	}
}
